import { ScanNotFound } from "@hobbyshopify/backend-client";
import { hasError } from "operation-result.js";
import { SubtreeController } from "subtree.js/core";
import { loadScan } from "../../core/ops/scan-ops";
import { ScanEditorController, type ScanEditorDeps } from "../../sections/scan-editor/scan-editor-controller";
import { EditScanActions, type EditScanKind, type EditScanMode, EditScanState } from "./edit-scan-model";

export type { EditScanKind, EditScanMode };

export type EditScanDeps = ScanEditorDeps;

export interface EditScanParams {
  readonly scanId: string;
  readonly kind: EditScanKind;
  readonly mode: EditScanMode;
  /** The scans to walk, in order, when opened from a list (Inventory › Review); null for one scan. */
  readonly queue: readonly string[] | null;
}

export interface EditScanRouting {
  /** Back to the list the scan was opened from. */
  back(): void;
  /** The next scan of the list, in place of this one. */
  open(scanId: string): void;
}

/**
 * Review screen for one scan: both reviewing a pending scan and re-opening a
 * decided one land here. The scan is fetched by id so the page survives refresh
 * and direct navigation; the editing itself is the shared `ScanEditorController`
 * section. Opened from a list, Skip and every decision move to the next scan
 * and the last one goes back, as the iOS review pager walks its queue.
 */
export class EditScanController extends SubtreeController implements EditScanActions {
  readonly state: EditScanState;
  readonly editor: ScanEditorController;
  readonly #next: string | null;
  #started = false;

  constructor(
    readonly params: EditScanParams,
    readonly deps: EditScanDeps,
    readonly routing: EditScanRouting,
  ) {
    super();
    const queue = params.queue ?? [];
    const index = queue.indexOf(params.scanId);
    this.#next = index >= 0 ? (queue[index + 1] ?? null) : null;
    this.state = new EditScanState(
      params.kind,
      params.mode,
      index >= 0 ? { index: index + 1, total: queue.length } : null,
      this.#next !== null,
    );
    this.subtree.put(EditScanState, this.state);
    this.subtree.put(EditScanActions, this);
    this.editor = this.own(
      new ScanEditorController(this.subtree, deps, {
        onConfirmed: () => this.#advance(),
        onRejected: () => this.#advance(),
      }),
    );

    this.sync(() => {
      const shopId = deps.storeRepository.shopId;
      if (shopId !== null && !this.#started) {
        this.#started = true;
        void this.#start(shopId);
      }
    }, [deps.storeRepository]);
  }

  cancel = () => {
    this.routing.back();
  };

  skip = () => {
    if (this.#next !== null) this.routing.open(this.#next);
  };

  retry = () => {
    const shopId = this.deps.storeRepository.shopId;
    if (shopId === null || this.state.status.value === "loading") return;
    this.state.status.value = "loading";
    void this.#start(shopId);
  };

  #advance(): void {
    if (this.#next !== null) this.routing.open(this.#next);
    else this.routing.back();
  }

  async #start(shopId: string): Promise<void> {
    const r = await loadScan({ ...this.deps, shopId, scanId: this.params.scanId }).catch((thrown: unknown) => {
      // A read that threw is a failed read: Retry reads again, rather than finding it still loading.
      if (!this.isDisposed) this.state.status.value = "error";
      throw thrown;
    });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.status.value = hasError(r, ScanNotFound) ? "notFound" : "error";
      return;
    }
    if (r.value.recognitionOutcome === "no_match") {
      this.state.noMatch.value = r.value;
      this.state.status.value = "ready";
      return;
    }
    this.editor.seed(r.value);
    this.state.status.value = "ready";
  }
}
